import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormCheckbox } from '@/components/forms/form-checkbox.js';
import { FormInputDate } from '@/components/forms/form-input-date.js';
import { FormInputField } from '@/components/forms/form-input-field.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { LoaderIcon } from '@/components/ui/loader-icon.js';
import { useRequiredOnboardingLessonsData } from '@/lib/api-call-hooks.js';
import { groupStudentsAndCreateOnboardingSessions } from '@/lib/api-calls.js';
import { isNotErrorString } from '@/lib/common-utils.js';
import { parseIntoPlainTimeInterval } from '@/lib/date-utils.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { formatMeetingBlockTimes } from '@/routes/_layout/scheduling/schedules/-components/formatting.js';
import { distinct, getOnboardingSessionConfig, isNotNullish } from '@airreading/common';
import { ClassTime, OnboardingSessionConfig, OnboardingSessionType, StudentOfSchool } from '@airreading/common/data';
import { ReactNode, useEffect, useMemo } from 'react';
import { DefaultValues } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { CoachAssignedProps } from '../$schoolId/students.js';

export interface GenerateOnboardingSessionProps {
  type: OnboardingSessionType;
  orgId: string;
  schoolTimeZone: string;
  students: StudentOfSchool[];
  trigger: ReactNode;
  title: string;
  onSuccess?: () => void;
  onCoachesAssigned?: (props: CoachAssignedProps) => void;
}

interface MakeFormSchemaProps {
  groupSize: number;
  isDiagnostic: boolean;
}

function makeFormSchema({ groupSize, isDiagnostic }: MakeFormSchemaProps) {
  return z
    .object({
      groupSize: z.coerce
        .number()
        .int()
        .min(1, { message: 'Group size must be at least 1' })
        .max(groupSize, {
          message: `Group size must be at most ${groupSize}`,
        }),
      meetingTimes: z.string().refine(
        (val) => {
          return isNotErrorString(parseIntoPlainTimeInterval(val));
        },
        { message: 'Invalid time format. Use HH:mm - HH:mm' },
      ),
      startPlainDate: z.instanceof(Temporal.PlainDate),
      autoCreateSessions: z.boolean(),
      autoCreateMakeupSessions: z.boolean(),
      makeupStartPlainDate: z.instanceof(Temporal.PlainDate).optional().nullable(),
      makeupEndPlainDate: z.instanceof(Temporal.PlainDate).optional().nullable(),
      prioritizeTeachersWithoutClasses: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (!isDiagnostic || !data.autoCreateMakeupSessions) {
        return;
      }

      if (!data.makeupStartPlainDate) {
        ctx.addIssue({
          path: ['makeupStartPlainDate'],
          code: z.ZodIssueCode.custom,
          message: 'Makeup diagnostic start date is required.',
        });
      }

      if (!data.makeupEndPlainDate) {
        ctx.addIssue({
          path: ['makeupEndPlainDate'],
          code: z.ZodIssueCode.custom,
          message: 'Makeup diagnostic end date is required.',
        });
      }

      if (!data.makeupStartPlainDate || !data.makeupEndPlainDate) {
        return;
      }

      if (Temporal.PlainDate.compare(data.makeupStartPlainDate, data.makeupEndPlainDate) > 0) {
        ctx.addIssue({
          path: ['makeupStartPlainDate'],
          code: z.ZodIssueCode.custom,
          message: 'Start date must be before end date.',
        });
        ctx.addIssue({
          path: ['makeupEndPlainDate'],
          code: z.ZodIssueCode.custom,
          message: 'End date must be after start date.',
        });
      }
    });
}

export const GenerateOnboardingSession = ({
  type,
  orgId,
  schoolTimeZone,
  students,
  trigger,
  title,
  onSuccess,
  onCoachesAssigned,
}: GenerateOnboardingSessionProps) => {
  const { toast } = useToast();
  const studentsWithEmptyClassTime = students.filter((student) => !student.requestedClassTime);
  const allClassTimes = students
    .map((student) => student.requestedClassTime)
    .filter(isNotNullish)
    .filter(distinct((ct) => ct.id));

  const hasMultipleClassTimes = allClassTimes.length > 1;
  const isDiagnostic = type === 'diagnostic';
  const showPrioritizeTeachersWithoutClasses = type !== 'practice' && type !== 'practice_google_meet';
  const {
    lessonName,
    learningPathName,
    groupSize: defaultGroupSize,
    scheduleDateKey,
  } = getOnboardingSessionConfig(type);
  const { startingLesson, learningPath, missingPredefinedLessons, isLoading } = useRequiredOnboardingLessonsData(
    lessonName,
    learningPathName,
  );

  // The compiler can't prove the returned zod schema isn't mutated later,
  // so it cannot optimize the component. useMemo is used because `formSchema`
  // feeds the form resolver, and recreating it every render resets form state.
  const formSchema = useMemo(
    () =>
      makeFormSchema({
        groupSize: defaultGroupSize,
        isDiagnostic,
      }),
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    [defaultGroupSize, isDiagnostic],
  );

  const { commonMeetingBlock, commonSchedule, defaultDate } = calculateSharedSchedule(allClassTimes, scheduleDateKey);

  const defaultValues: DefaultValues<z.infer<typeof formSchema>> = {
    groupSize: defaultGroupSize,
    meetingTimes: commonMeetingBlock ? formatMeetingBlockTimes(commonMeetingBlock) : undefined,
    startPlainDate: defaultDate || undefined,
    autoCreateSessions: false,
    autoCreateMakeupSessions: false,
    makeupStartPlainDate: undefined,
    makeupEndPlainDate: undefined,
    prioritizeTeachersWithoutClasses: false,
  };

  const onSubmit = ({ formData, reset, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const {
      startPlainDate,
      groupSize,
      meetingTimes,
      autoCreateSessions,
      autoCreateMakeupSessions,
      makeupStartPlainDate,
      makeupEndPlainDate,
      prioritizeTeachersWithoutClasses,
    } = formData;
    const meetingInterval = parseIntoPlainTimeInterval(meetingTimes);
    if (!isNotErrorString(meetingInterval)) {
      toast({
        title: 'Invalid class time format',
        description: 'Please use the format HH:mm - HH:mm',
        variant: 'destructive',
      });
      return;
    }
    if (!learningPath || !startingLesson) {
      toast({
        title: 'Error creating onboarding sessions',
        description: 'Missing predefined lesson or learning path',
        variant: 'destructive',
      });
      return;
    }
    const { start: startPlainTime, end: endPlainTime } = meetingInterval;

    return groupStudentsAndCreateOnboardingSessions({
      startPlainDate,
      startPlainTime,
      endPlainTime,
      learningPathId: learningPath.id,
      lessonId: startingLesson.id,
      groupSize,
      studentIds: students.map((student) => student.id),
      scheduleId: commonSchedule.id,
      autoCreateSessions,
      prioritizeTeachersWithoutClasses,
      type,
      orgId,
      ...(isDiagnostic && autoCreateMakeupSessions && makeupStartPlainDate && makeupEndPlainDate
        ? {
            autoCreateMakeupSessions,
            makeupStartPlainDate,
            makeupEndPlainDate,
          }
        : {}),
    })
      .then(({ studentGroupsCount, availableCoachesCount, coachesAssignedCount, sessionGenerationWarnings }) => {
        toast({
          title: 'Onboarding sessions created',
          description: `Created ${studentGroupsCount} classes for selected students`,
        });
        for (const warning of sessionGenerationWarnings ?? []) {
          toast({
            title: warning,
            variant: 'destructive',
          });
        }
        onSuccess?.();
        reset();
        closeModal();

        if (studentGroupsCount > 0) {
          onCoachesAssigned?.({ availableCoachesCount, coachesAssignedCount });
        }
      })
      .catch((error) => {
        toast({
          title: 'Error creating onboarding sessions',
          description: airServerErrorFrom(error)?.message || 'Unknown error',
          variant: 'destructive',
        });
      });
  };

  return (
    <FormModal
      modalTitle={title}
      formSchema={formSchema}
      trigger={trigger}
      defaultValues={defaultValues}
      dialogContentProps={{ className: 'lg:max-w-screen-sm' }}
      submitButtonProps={{
        disabled:
          isLoading || studentsWithEmptyClassTime.length > 0 || hasMultipleClassTimes || missingPredefinedLessons,
      }}
      onSubmit={onSubmit}
    >
      {({ form }) => {
        if (isLoading) {
          return <LoaderIcon />;
        }
        if (studentsWithEmptyClassTime.length > 0) {
          return (
            <div className="mt-4">
              Following students do not have class times assigned. Please assign class times to all students before
              generating onboarding sessions.
              <ul className="pl-8">
                {studentsWithEmptyClassTime.map((student) => (
                  <li key={student.id} className="list-disc">
                    {student.firstName} {student.lastName}
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        if (hasMultipleClassTimes) {
          return (
            <div className="mt-4">
              Selected students must all have the same class time. Please use the table filter to select only students
              with the same class time.
            </div>
          );
        }

        if (missingPredefinedLessons) {
          return (
            <div className="mt-4">
              Cannot find lesson: [{lessonName}] or learning path: [{learningPathName}] required for creating {type}{' '}
              classes. Please check the existing lessons and learning paths, and contact Engineering team.
            </div>
          );
        }

        const { watch, trigger } = form;
        const [makeupStartPlainDate, makeupEndPlainDate, autoCreateMakeupSessions] = watch([
          'makeupStartPlainDate',
          'makeupEndPlainDate',
          'autoCreateMakeupSessions',
        ]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (!isDiagnostic || !autoCreateMakeupSessions || !makeupStartPlainDate || !makeupEndPlainDate) {
            return;
          }
          trigger(['makeupStartPlainDate', 'makeupEndPlainDate']);
        }, [isDiagnostic, autoCreateMakeupSessions, makeupStartPlainDate, makeupEndPlainDate, trigger]);

        return (
          <div className="grid grid-cols-2 items-start gap-2 py-4">
            <FormInputDate name="startPlainDate" label="Class date" form={form} triggerButtonProps={{ size: 'sm' }} />
            <FormInputField
              name="groupSize"
              label="Group size"
              form={form}
              className="flex flex-col"
              inputProps={{ type: 'number' }}
            />
            <div className="col-span-2">
              <div className="mt-2 text-red-700 text-lg">
                The class time you enter here is in school's time zone:{' '}
                <span className="font-bold">{schoolTimeZone}</span>
              </div>
              <FormInputField
                name="meetingTimes"
                label="Class time (Format: HH:mm - HH:mm)"
                form={form}
                inputProps={{
                  placeholder: 'e.g. 13:00 - 14:00',
                }}
              />
            </div>
            {isDiagnostic && (
              <div className="col-span-2">
                <FormCheckbox
                  name="autoCreateMakeupSessions"
                  label="Auto-generate makeup diagnostic classes"
                  form={form}
                />
              </div>
            )}
            {isDiagnostic && autoCreateMakeupSessions && (
              <>
                <FormInputDate name="makeupStartPlainDate" label="Makeup diagnostic start date" form={form} />
                <FormInputDate name="makeupEndPlainDate" label="Makeup diagnostic end date" form={form} />
              </>
            )}
            <div className="col-span-2">
              <FormCheckbox name="autoCreateSessions" label="Auto-create sessions after creating classes" form={form} />
            </div>
            {showPrioritizeTeachersWithoutClasses && (
              <div className="col-span-2">
                <FormCheckbox
                  name="prioritizeTeachersWithoutClasses"
                  label="Prioritize teachers without classes"
                  form={form}
                  labelTooltip="Assign classes to eligible teachers without active classes before using the current grade-based priority."
                />
              </div>
            )}
          </div>
        );
      }}
    </FormModal>
  );
};

function calculateSharedSchedule(
  allClassTimes: ClassTime[],
  scheduleDateKey: OnboardingSessionConfig['scheduleDateKey'],
) {
  const commonClassTime = allClassTimes[0];
  const commonSchedule = commonClassTime?.schedule;
  const commonMeetingBlock = commonClassTime?.meetingBlocks[0];
  const defaultDate = commonSchedule?.[scheduleDateKey];

  return {
    commonClassTime,
    commonSchedule,
    commonMeetingBlock,
    defaultDate,
  };
}
